import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export type RevealState = 'idle' | 'armed' | 'shown';

// useLayoutEffect warns during SSR; the server render never arms anything.
const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * Plays a section's entrance once, when it scrolls into view.
 *
 * The server renders everything visible (`idle`), so the page reads fine
 * without JavaScript and crawlers see the final state. After hydration, a
 * section that is still below the fold is `armed` (hidden by CSS) before the
 * visitor can see it, and flips to `shown` when it enters the viewport.
 * Sections already on screen, and visitors who prefer reduced motion, are
 * never animated.
 */
export function useReveal<T extends HTMLElement>(threshold = 0.25) {
  const ref = useRef<T>(null);
  const [state, setState] = useState<RevealState>('idle');

  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const rect = node.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.9) return;
    setState('armed');
  }, []);

  useEffect(() => {
    if (state !== 'armed') return;
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setState('shown');
          observer.disconnect();
        }
      },
      { threshold, rootMargin: '0px 0px -8% 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [state, threshold]);

  return { ref, state };
}
